import { useEffect, useState } from "react";

/**
 * useDevicePlatform — returns "ios" | "android" | "desktop" based on the
 * navigator user-agent. Defaults to "desktop" during SSR / before mount.
 *
 * Used by App Store Triage to show platform-appropriate cancellation copy.
 */
export default function useDevicePlatform() {
  const [platform, setPlatform] = useState("desktop");

  useEffect(() => {
    if (typeof navigator === "undefined") return;
    const ua = (navigator.userAgent || "").toLowerCase();
    if (/iphone|ipad|ipod/.test(ua)) {
      setPlatform("ios");
    } else if (/android/.test(ua)) {
      setPlatform("android");
    } else if (
      // Some iPads now report as Mac — detect via touch + macOS combo.
      /macintosh/.test(ua) && (navigator.maxTouchPoints || 0) > 1
    ) {
      setPlatform("ios");
    } else {
      setPlatform("desktop");
    }
  }, []);

  return platform;
}

/**
 * detectAppStore — given a subscription name, returns "apple" | "google" | null.
 * Matches obvious in-app billing surfaces (iTunes, App Store, Google Play, etc.)
 * but stays conservative to avoid false positives on company names that just
 * happen to contain "Apple" or "Google".
 */
export function detectAppStore(subName) {
  const n = (subName || "").toLowerCase().trim();
  if (!n) return null;
  // Apple billing surfaces
  if (/\bapple\.com\/bill|itunes|app\s*store|apple subscription|apple music subscription/.test(n)) {
    return "apple";
  }
  // Common pattern: standalone "apple" usually = Apple Inc charges
  if (/^apple\b/.test(n)) return "apple";
  // Google Play billing surfaces
  if (/google\s*play|play\s*store|google\s*subscription|google\s*billing|google\.com\/play/.test(n)) {
    return "google";
  }
  return null;
}
